import React, { useState, useMemo } from 'react';
import {
    Grid,
    Typography,
    Box,
    CardContent,
    Divider,
    Chip
} from '@mui/material';
import { StyledCard } from "../../../../RegulatoryMember/ExitRequestDetail/styles";
import {
    cardContentStyles,
    cardTransition
} from "../../../ExitRequestResponseUnified/ExitRequestResponseStyles";

// Local imports
import { TextViewerPopup } from './TextViewerPopup';
import { DetailField } from './DetailField';
import { DescriptionField } from './DescriptionField';
import {
    FIELD_LABELS,
    DEFAULT_MESSAGES,
    ICON_LABELS
} from './RequestDetailsTab.constants';
import {
    formatDate,
    getRestrictionChipConfig,
    extractRequestDetails
} from './RequestDetailsTab.utils';
import { useSelector } from 'react-redux';

export const RequestDetailsTab = ({ requestData, theme }) => {
    // ================ Local State ================
    const [popupState, setPopupState] = useState({
        open: false,
        title: '',
        content: ''
    });

    // ================ Handlers ================
    const handleOpenPopup = (title, content) => {
        setPopupState({
            open: true,
            title,
            content
        });
    };

    const handleClosePopup = () => {
        setPopupState({
            open: false,
            title: '',
            content: ''
        });
    };

    // ================ Data Processing ================
    const details = extractRequestDetails(requestData);
    const userRegions = useSelector((state) => state.user.regions || []);
    const countriesInMyRegions = useMemo(() => {
        const countries = requestData?.exitRequest?.countries || [];
        if (!countries.length) return [];
        if (!userRegions.length) return countries;

        const normalizedRegions = userRegions.map((region) =>
            (region.name || region.regionName || region)?.toLowerCase()
        );

        return countries.filter((country) =>
            normalizedRegions.includes((country.regionName || '').toLowerCase())
        );
    }, [requestData?.exitRequest?.countries, userRegions]);

    // ================ Render Conditions ================
    if (!details) {
        return (
            <StyledCard sx={cardTransition(theme)}>
                <CardContent sx={cardContentStyles}>
                    <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                        {DEFAULT_MESSAGES.NO_DATA}
                    </Typography>
                </CardContent>
            </StyledCard>
        );
    }

    // ================ Main Render ================
    return (
        <Box sx={styles.container}>
            <Grid item xs={12} sm={12}>
                <StyledCard sx={cardTransition(theme)}>
                    <CardContent sx={cardContentStyles}>
                        {/* Request Information Section */}
                        <Grid container spacing={2}>
                            <Grid item xs={12} md={12}>
                                <DetailField
                                    label={FIELD_LABELS.CREATED_ON}
                                    value={formatDate(details.createdAt)}
                                    theme={theme}
                                />
                                <DetailField
                                    label={FIELD_LABELS.INGREDIENT_NAME}
                                    value={details.ingredientName}
                                    theme={theme}
                                />
                                <DetailField
                                    label={FIELD_LABELS.CAS_NUMBER}
                                    value={details.casNumber}
                                    theme={theme}
                                />
                                <DetailField
                                    label={FIELD_LABELS.RESTRICTION}
                                    value={details.restrictionType}
                                    isChip={!!details.restrictionType && details.restrictionType !== DEFAULT_MESSAGES.NOT_AVAILABLE}
                                    chipConfig={details.restrictionType ? getRestrictionChipConfig(details.restrictionType) : null}
                                    theme={theme}
                                />
                            </Grid>

                            <Grid item xs={12} md={12} style={{paddingTop:"0px"}}>
                                <DetailField
                                    label={details.primaryDateType ? details.primaryDateType.replace(/_/g, ' ') + ':' : FIELD_LABELS.PRIMARY_DATE}
                                    value={formatDate(details.primaryDate)}
                                    theme={theme}
                                />
                                {details.secondaryDate && (
                                    <DetailField
                                        label={details.secondaryDateType ? details.secondaryDateType.replace(/_/g, ' ') + ':' : FIELD_LABELS.SECONDARY_DATE}
                                        value={formatDate(details.secondaryDate)}
                                        theme={theme}
                                    />
                                )}
                                {details.ulInternalDate && (
                                    <DetailField
                                        label={FIELD_LABELS.UL_INTERNAL_DATE}
                                        value={formatDate(details.ulInternalDate)}
                                        theme={theme}
                                    />
                                )}
                                <DetailField
                                    label={FIELD_LABELS.ISSUED_BY}
                                    value={details.issuedBy}
                                    theme={theme}
                                />
                                <DetailField
                                    label={FIELD_LABELS.EMAIL}
                                    value={details.email || DEFAULT_MESSAGES.NOT_AVAILABLE}
                                    isEmail={true}
                                    theme={theme}
                                />
                                <DetailField
                                    label={FIELD_LABELS.CATEGORY}
                                    value={requestData?.exitRequest?.categoryName || DEFAULT_MESSAGES.NOT_AVAILABLE}
                                    theme={theme}
                                />
                                <Box sx={{ mt: 1 }}>
                                    <Typography variant="body2" color={theme.palette.text.secondary} sx={{ fontWeight: 600 }}>
                                        Countries
                                    </Typography>
                                    {countriesInMyRegions.length ? (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                            {countriesInMyRegions.map((country) => (
                                                <Chip
                                                    key={country.id || country.countryCode}
                                                    label={`${country.name}`}
                                                    size="small"
                                                    color="primary"
                                                />
                                            ))}
                                        </Box>
                                    ) : (
                                        <Typography variant="body2" color={theme.palette.text.secondary} sx={{ mt: 0.5 }}>
                                            No assigned countries for your regions
                                        </Typography>
                                    )}
                                </Box>
                            </Grid>
                        </Grid>

                        <Divider sx={styles.divider} />

                        {/* Description Section */}
                        <DescriptionField
                            label={FIELD_LABELS.INGREDIENT_DESCRIPTION}
                            content={details.ingredientDescription}
                            onExpand={() => handleOpenPopup(
                                FIELD_LABELS.INGREDIENT_DESCRIPTION.replace(':', ''),
                                details.ingredientDescription
                            )}
                            iconLabel={ICON_LABELS.EXPAND_DESCRIPTION}
                            theme={theme}
                        />

                        <Box sx={{ mt: 1 }}>
                            <DescriptionField
                                label={FIELD_LABELS.ADDITIONAL_NOTES}
                                content={details.additionalInfo}
                                onExpand={() => handleOpenPopup(
                                    FIELD_LABELS.ADDITIONAL_NOTES.replace(':', ''),
                                    details.additionalInfo
                                )}
                                iconLabel={ICON_LABELS.EXPAND_NOTES}
                                theme={theme}
                            />
                        </Box>
                    </CardContent>
                </StyledCard>
            </Grid>

            {/* Text Viewer Popup */}
            <TextViewerPopup
                open={popupState.open}
                onClose={handleClosePopup}
                title={popupState.title}
                content={popupState.content}
                theme={theme}
            />
        </Box>
    );
};

// ================ Component Styles ================
const styles = {
    container: {
        maxHeight: '100vh',
        overflowY: 'auto'
    },
    divider: {
        my: 2
    }
};